import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, Sparkles, BadgeCheck, Lock, ArrowRight, Users, RotateCcw, FileBadge, Fuel, AlertTriangle, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RiskBadge } from "@/components/RiskBadge";
import { LiveDataBadge } from "@/components/LiveDataBadge";
import { CheckByRut } from "@/components/CheckByRut";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const CURRENT_YEAR = new Date().getFullYear();

export const CheckPreview = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [mode, setMode] = useState(() =>
    typeof window !== "undefined" && new URLSearchParams(window.location.search).get("modo") === "rut"
      ? "rut" : "patente");
  const [patente, setPatente] = useState("");
  const [anio, setAnio] = useState("");
  const [loading, setLoading] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  // Llegada desde el CTA "Obtener deuda TAG" (/?modo=rut#consulta-patente):
  // la SPA renderiza después del hash nativo → hacemos el scroll manualmente.
  useEffect(() => {
    if (window.location.hash === "#consulta-patente") {
      setTimeout(() => document.getElementById("consulta-patente")
        ?.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
    }
  }, []);

  const consultar = async () => {
    if (!patente.trim()) {
      setError("Ingresa la patente del vehículo.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/api/simulator/check", {
        patente: patente.trim(),
        anio: anio ? Number(anio) : null,
      });
      if (data?.retry) {
        setError(data.resumen || "Alta demanda en este momento. Vuelve a intentar en unos segundos.");
      } else {
        setResult(data);
      }
    } catch (err) {
      const st = err?.response?.status;
      if (st === 429 || err?.code === "ECONNABORTED" || !err?.response) {
        // Rate-limit / timeout / red: la consulta gratis NO se consume; invitamos a reintentar.
        setError("Alta demanda en este momento. Tu consulta gratis sigue disponible: vuelve a intentar en unos segundos.");
      } else {
        setError("No pudimos consultar la patente. Inténtalo nuevamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const verInforme360 = async () => {
    setGenLoading(true);
    try {
      const { data } = await api.post("/api/report/express", {
        patente: patente.trim(),
        anio: anio ? Number(anio) : null,
        origin_url: window.location.origin,
      });
      navigate(`/informe-express/${data.report_code}`);
    } catch (err) {
      if (err?.response?.status === 402) {
        // Cupo agotado: mostramos el muro de pago en lugar del informe.
        setResult({ quota_exceeded: true, ...(err.response.data?.detail || {}) });
      } else if (err?.response?.status === 429 || err?.code === "ECONNABORTED" || !err?.response) {
        setError("Alta demanda en este momento. Vuelve a intentar el Informe 360 en unos segundos.");
      } else {
        setError("No pudimos generar el informe. Inténtalo nuevamente.");
      }
      setGenLoading(false);
    }
  };

  const buyInforme = async () => {
    if (!user) {
      login();
      return;
    }
    setGenLoading(true);
    try {
      const { data } = await api.post("/api/payments/checkout", {
        package_id: "informe",
        origin_url: window.location.origin,
        patente: patente.trim(),
        anio: anio ? Number(anio) : null,
      });
      window.location.href = data.url;
    } catch {
      setError("No pudimos iniciar el pago. Inténtalo nuevamente.");
      setGenLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setError("");
    setPatente("");
    setAnio("");
  };

  const real = result?.datos_reales?.real;

  return (
    <section className="bg-[#13315C] relative overflow-hidden" id="consulta-patente">
      <div className="absolute inset-0 tf-grain opacity-20" />
      <div className="absolute -top-24 right-10 h-64 w-64 rounded-full bg-[#F5B301]/10 blur-3xl" />
      <div className="absolute -bottom-24 left-10 h-64 w-64 rounded-full bg-[#1F9BD7]/10 blur-3xl" />
      <div className="relative max-w-5xl mx-auto px-5 md:px-8 py-16 md:py-20">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#F5B301]/30 bg-[#F5B301]/10 px-3 py-1.5 text-xs font-semibold text-[#F5B301] mb-5">
            <Sparkles size={14} /> Gratis · sin registro · al instante
          </div>

          <div className="mb-5 flex justify-center">
            <div className="inline-flex rounded-full bg-white/10 p-1 border border-white/15" data-testid="check-mode-toggle">
              <button
                onClick={() => setMode("patente")}
                className={`px-5 py-2.5 min-h-[44px] rounded-full text-sm font-bold transition-colors active:scale-95 ${mode === "patente" ? "bg-[#F5B301] text-[#13315C]" : "text-white/70 hover:text-white"}`}
                data-testid="check-mode-patente"
              >
                Por patente
              </button>
              <button
                onClick={() => setMode("rut")}
                className={`px-5 py-2.5 min-h-[44px] rounded-full text-sm font-bold transition-colors active:scale-95 ${mode === "rut" ? "bg-[#F5B301] text-[#13315C]" : "text-white/70 hover:text-white"}`}
                data-testid="check-mode-rut"
              >
                Por RUT
              </button>
            </div>
          </div>

          {mode === "patente" ? (
            <>
              <h2 className="font-display font-black text-3xl md:text-4xl text-white">Consulta tu vehículo por patente</h2>
              <p className="mt-3 text-white/60">
                Ingresa la patente y obtén al instante los <strong className="text-white">datos reales del vehículo</strong>:
                marca, modelo, versión, año y ficha técnica verificada. Sin costo y sin crear cuenta.
              </p>
            </>
          ) : (
            <>
              <h2 className="font-display font-black text-3xl md:text-4xl text-white">Consulta los vehículos por RUT</h2>
              <p className="mt-3 text-white/60">
                Ingresa el RUT del titular y obtén el <strong className="text-white">Informe 360 por RUT</strong>:
                identidad validada, vehículos y propiedades a su nombre. Verificación del titular incluida.
              </p>
            </>
          )}

          {mode === "patente" && (
          <>
          <div className="mt-7 flex flex-col sm:flex-row gap-2 max-w-xl mx-auto">
            <Input
              value={patente}
              onChange={(e) => setPatente(e.target.value.toUpperCase())}
              placeholder="ABCD12"
              maxLength={6}
              className="uppercase h-12 bg-white text-[#13315C] border-0 text-center sm:text-left font-mono-tf text-lg"
              data-testid="check-patente-input"
              onKeyDown={(e) => e.key === "Enter" && consultar()}
            />
            <Input
              value={anio}
              onChange={(e) => setAnio(e.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder="Año"
              inputMode="numeric"
              maxLength={4}
              min={1950}
              max={CURRENT_YEAR + 1}
              className="h-12 bg-white text-[#13315C] border-0 text-center font-mono-tf text-lg sm:w-28"
              data-testid="check-anio-input"
              onKeyDown={(e) => e.key === "Enter" && consultar()}
            />
            <Button onClick={consultar} disabled={loading} className="h-12 px-6 bg-[#F5B301] hover:bg-[#dca400] text-[#13315C] font-bold rounded-md whitespace-nowrap" data-testid="check-btn">
              {loading ? <Loader2 className="animate-spin" size={18} /> : <><Search size={18} className="mr-1.5" /> Consultar gratis</>}
            </Button>
          </div>
          {error && <p className="mt-3 text-sm text-red-300" data-testid="check-error">{error}</p>}

          {!result && (
            <div className="mt-6 flex items-center justify-center">
              <LiveDataBadge />
            </div>
          )}
          </>
          )}

          {mode === "rut" && <CheckByRut />}
        </div>

        <>
          {mode === "patente" && result?.quota_exceeded && (
            <div
              className="tf-fade-up mt-8 max-w-2xl mx-auto bg-white rounded-2xl p-6 md:p-8 text-center shadow-xl"
              data-testid="check-paywall"
            >
              <div className="mx-auto w-14 h-14 rounded-full bg-[#F5B301]/15 flex items-center justify-center">
                <Lock size={26} className="text-[#b6840a]" />
              </div>
              <h3 className="font-display font-black text-2xl text-[#13315C] mt-4">Ya usaste tu consulta gratis</h3>
              <p className="text-slate-500 mt-2">
                {result.authenticated
                  ? "Obtén el Informe Transfiérelo 360 completo de esta patente: identidad verificada, alerta de robo, SOAP, riesgo y más."
                  : "Inicia sesión para acceder al Informe Transfiérelo 360 completo y seguir consultando patentes."}
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                {result.authenticated ? (
                  <Button onClick={buyInforme} disabled={genLoading} className="h-12 px-7 bg-[#13315C] hover:bg-[#1C4076] text-white font-semibold rounded-md" data-testid="paywall-buy-btn">
                    {genLoading ? <Loader2 className="animate-spin mr-2" size={18} /> : <FileBadge size={17} className="mr-2" />}
                    Obtener Informe 360 · ${(result.precio_informe || 4990).toLocaleString("es-CL")}
                  </Button>
                ) : (
                  <Button onClick={login} className="h-12 px-7 bg-[#13315C] hover:bg-[#1C4076] text-white font-semibold rounded-md" data-testid="paywall-login-btn">
                    Iniciar sesión <ArrowRight size={17} className="ml-1.5" />
                  </Button>
                )}
                <Button onClick={reset} variant="outline" className="h-12 px-7 rounded-md border-slate-300" data-testid="paywall-reset-btn">
                  <RotateCcw size={15} className="mr-1.5" /> Volver
                </Button>
              </div>
              <p className="text-xs text-slate-400 mt-4">Cada usuario tiene una consulta gratis. El Informe 360 incluye los antecedentes completos.</p>
            </div>
          )}

          {mode === "patente" && result && !result.quota_exceeded && (
            <div
              className="tf-fade-up mt-8 max-w-3xl mx-auto bg-white rounded-2xl p-6 md:p-8 text-left shadow-xl"
              data-testid="check-result"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Patente consultada</p>
                  <p className="font-mono-tf font-black text-2xl text-[#13315C] mt-0.5" data-testid="check-result-patente">{result.patente}</p>
                  <p className="text-slate-600 mt-1" data-testid="check-result-vehiculo">
                    {result.vehiculo?.marca || result.vehiculo?.modelo
                      ? `${result.vehiculo?.marca || ""} ${result.vehiculo?.modelo || ""}`.trim()
                      : "Vehículo"}
                    {result.vehiculo?.anio ? ` · ${result.vehiculo.anio}` : ""}
                    {result.vehiculo?.tipo ? ` · ${result.vehiculo.tipo}` : ""}
                  </p>
                  {result.vehiculo?.version && (
                    <p className="text-slate-400 text-sm mt-0.5" data-testid="check-result-version">{result.vehiculo.version}</p>
                  )}
                  {real ? (
                    <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-1 text-xs font-bold text-emerald-700" data-testid="check-result-verified">
                      <BadgeCheck size={13} /> Identidad verificada · Boostr (SRCeI)
                    </span>
                  ) : (
                    <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-slate-100 border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-500">
                      Identidad del vehículo: por confirmar
                    </span>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Semáforo de riesgo</p>
                  {result.encargo_robo_alerta
                    ? <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 border border-red-200 px-3 py-1.5 text-sm font-bold text-red-600" data-testid="check-semaforo-alerta">
                        <AlertTriangle size={14} /> Alerta de robo
                      </span>
                    : result.documental_disponible
                      ? <RiskBadge color={result.semaforo?.color} level={result.semaforo?.level} score={result.semaforo?.score} size="lg" />
                      : <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 border border-slate-200 px-3 py-1.5 text-sm font-bold text-slate-500" data-testid="check-semaforo-pending">
                          <Lock size={14} /> Por evaluar
                        </span>}
                </div>
              </div>

              {(result.encargo_robo_real || result.soap_real || result.multas_real || result.rt_real || result.tag_real) && (
                <div className="mt-4 flex flex-wrap gap-2" data-testid="check-verified-chips">
                  {result.encargo_robo_real && (
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold border ${result.encargo_robo_alerta ? "bg-red-50 border-red-200 text-red-600" : "bg-emerald-50 border-emerald-200 text-emerald-700"}`}>
                      {result.encargo_robo_alerta ? <AlertTriangle size={12} /> : <ShieldCheck size={12} />}
                      {result.encargo_robo_alerta ? "Alerta de robo activa" : "Sin alerta de robo"}
                    </span>
                  )}
                  {result.soap_real && (
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold border ${result.soap_vigente ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}>
                      <BadgeCheck size={12} /> SOAP {result.soap_vigente ? "vigente" : "no vigente"}
                    </span>
                  )}
                  {result.multas_real && (
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold border ${result.multas_tiene ? "bg-red-50 border-red-200 text-red-600" : "bg-emerald-50 border-emerald-200 text-emerald-700"}`}>
                      <BadgeCheck size={12} /> {result.multas_tiene ? "Con multas" : "Sin multas"}
                    </span>
                  )}
                  {result.rt_real && (
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold border ${result.rt_vigente ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}>
                      <BadgeCheck size={12} /> Rev. técnica {result.rt_vigente ? "vigente" : "vencida"}
                    </span>
                  )}
                  {result.tag_real && (
                    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold border bg-emerald-50 border-emerald-200 text-emerald-700">
                      <BadgeCheck size={12} /> TAG consultado
                    </span>
                  )}
                </div>
              )}

              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                <div className="rounded-lg bg-slate-50 border border-slate-100 p-3.5 flex items-center gap-3">
                  {result.documental_disponible ? <Users size={18} className="text-slate-400" /> : <Fuel size={18} className="text-slate-400" />}
                  <div>
                    <p className="text-xs text-slate-400">{result.documental_disponible ? "N° de propietarios" : "Combustible"}</p>
                    <p className="font-bold text-[#13315C]">
                      {result.documental_disponible ? (result.n_propietarios ?? "—") : (result.vehiculo?.combustible || "—")}
                    </p>
                  </div>
                </div>
                <div className="rounded-lg bg-slate-50 border border-slate-100 p-3.5 flex items-center gap-3">
                  <Sparkles size={18} className="text-[#F5B301]" />
                  <div>
                    <p className="text-xs text-slate-400">Resumen</p>
                    <p className="font-semibold text-[#13315C] text-sm leading-snug">{result.resumen || "Consulta lista."}</p>
                  </div>
                </div>
              </div>

              <div className="mt-5 rounded-lg border border-slate-200 p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                  <Lock size={12} /> Incluido en el Informe Transfiérelo 360 completo
                </p>
                <div className="mt-3 grid sm:grid-cols-2 gap-x-5 gap-y-2">
                  {(result.preview_items || []).map((it) => (
                    <div key={it.label} className="flex items-center gap-2 text-sm text-slate-500">
                      <Lock size={13} className="text-slate-300 shrink-0" /> {it.label}
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Button onClick={verInforme360} disabled={genLoading} className="flex-1 h-12 bg-[#13315C] hover:bg-[#1C4076] text-white font-semibold rounded-md group" data-testid="check-ver-informe-btn">
                  {genLoading ? <Loader2 className="animate-spin mr-2" size={18} /> : <FileBadge size={17} className="mr-2" />}
                  Ver Informe 360 completo
                </Button>
                <Button onClick={user ? () => navigate("/nueva-transferencia") : login} variant="outline" className="flex-1 h-12 border-[#10B981] text-[#0e9d6e] hover:bg-[#10B981]/10 hover:text-[#0e9d6e] font-semibold rounded-md group" data-testid="check-iniciar-transferencia-btn">
                  Iniciar transferencia <ArrowRight size={17} className="ml-1.5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
              <button onClick={reset} className="mt-4 inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-[#13315C] transition-colors" data-testid="check-reset-btn">
                <RotateCcw size={14} /> Consultar otra patente
              </button>
            </div>
          )}
        </>
      </div>
    </section>
  );
};
